import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useLocation } from 'react-router';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { isTemplateNew } from '../../../../models/UiUtils';
import { invalidateAllMyTemplatesFilter, invalidateMyTemplates } from '../../../../queries';
import { invalidateEmailAllCategoryTemplates } from '../../../../queries/EmailCampaign';
import { ITemplateCard, KnownCategories } from '../../../../viewmodels/AppViewModels';
import FinraImage from '../../../assets/finra.png';
import { bs } from '../../../styles/styles';
import { CampaignExpiryHint } from '../../campaigns/CampaignExpiryHint';
import { OpenLetter } from '../../svgs/icons/OpenLetter';
import { SendNow } from '../../svgs/icons/SendNow';
import { SocialMediaIndicatorIcon } from '../../svgs/icons/SocialMediaIndicatorIcon';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { TemplateKeywords } from '../TemplateKeywords';
import {
	CampaignSummaryFromContent,
	CampaignSummaryFromContentShort,
	CampaignTemplateCardCta,
	CampaignTemplateCardMenu,
} from './presentation';
import { styleSheet } from './styles';

export interface ICampaignTemplateCardProps extends IImpersonationContextComponentProps {
	card: ITemplateCard;
	category?: KnownCategories;
	className?: string;
	ctaText?: string;
	industry?: Api.Industry;
	isAccountTemplate?: boolean;
	isFeatured?: boolean;
	isFinraReviewed?: boolean;
	onCtaClicked?(e?: React.MouseEvent<Element>): void;
	onDelete?(): void;
	onShare?(isShared: boolean): void;
	onSocialClicked?(e: React.MouseEvent<Element>): void;
	rowIndex?: number;
	styles?: StyleDeclarationValue[];
	templateIndex?: number;
	useWholeCardAsCTA?: boolean;
}

const CampaignTemplateCardBase = ({
	card,
	category,
	ctaText,
	impersonationContext,
	industry,
	isAccountTemplate,
	isFeatured,
	isFinraReviewed,
	onCtaClicked,
	onDelete,
	onShare,
	onSocialClicked,
	rowIndex,
	styles,
	templateIndex,
	useWholeCardAsCTA,
}: ICampaignTemplateCardProps) => {
	const userSession = useUserSession();
	const loc = useLocation();
	const [showSocialPopover, , setShowSocialPopover] = useLambda(false);
	const expirationDate = card?.schedule?.expirationDate ? moment(card?.schedule?.expirationDate) : undefined;
	// intentionally disabling social media indicator while under impersonation context until we have compliance figured out
	const containsSocialMedia =
		userSession?.account?.features?.socialMedia?.enabled && card?.associatedTemplates?.length > 0;
	const showingSocialMedia = !impersonationContext?.isValid && !loc.pathname?.includes('ghost') && containsSocialMedia;
	const isMyTemplate = category === KnownCategories.MyTemplates;

	const onSocialMediaIndicatorClicked = (e: React.MouseEvent<SVGSVGElement>) => {
		if (useWholeCardAsCTA) {
			return;
		}
		setShowSocialPopover(false)();
		onSocialClicked?.(e);
	};

	const isNew = isTemplateNew(card);
	const handleInvalidateTemplates = () => {
		invalidateEmailAllCategoryTemplates();
		invalidateMyTemplates({ templateType: Api.TemplateType.Email });
		invalidateAllMyTemplatesFilter();
	};
	const canEditKeywords =
		card?.creator?.id === userSession.user.id || userSession.userRole.toLocaleLowerCase().includes('admin');
	const hasKeywords = card?.keywords && card.keywords.length > 0;
	const showKeywords = canEditKeywords || hasKeywords;

	return (
		<div
			className={css(styleSheet.container, isFeatured && styleSheet.featured, ...(styles || []))}
			onClick={useWholeCardAsCTA ? onCtaClicked : undefined}
		>
			{rowIndex === 0 && isFeatured && templateIndex === 0 && <SendNow className={css(styleSheet.sendNow)} />}
			{isAccountTemplate ? (
				<CampaignTemplateCardMenu card={card} userSession={userSession} onDelete={onDelete} onShare={onShare} />
			) : null}
			{rowIndex === 0 && isFeatured && templateIndex === 2 && <OpenLetter className={css(styleSheet.openLetter)} />}
			<div className={css(styleSheet.headerContainer)}>
				<div
					className={css(styleSheet.nameContainer, isFeatured && rowIndex === 0 ? styleSheet.flagHighlight : undefined)}
				>
					<div className={css(styleSheet.textContentTwoLines)} onClick={onCtaClicked}>
						{card.name || 'Custom Post'}{' '}
					</div>
					{expirationDate ? <CampaignExpiryHint expirationDate={expirationDate} /> : null}
					{isNew ? <span className={css(styleSheet.newItem)}>(New!)</span> : null}
				</div>
				{showingSocialMedia ? (
					<TinyPopover
						align='end'
						anchor={
							<SocialMediaIndicatorIcon
								className={css(bs.cursorPointer, styleSheet.socialMediaTag)}
								onClick={onSocialMediaIndicatorClicked}
								onMouseEnter={setShowSocialPopover(true)}
								onMouseLeave={setShowSocialPopover(false)}
							/>
						}
						anchorStyles={[styleSheet.popoverAnchor]}
						dismissOnOutsideAction={true}
						isOpen={showSocialPopover}
						placement={['top', 'left', 'bottom']}
						type={PopoverType.lightBlue}
					>
						<div className={css(styleSheet.popover)}>Also available for social media. </div>
					</TinyPopover>
				) : (
					<div className={css(styleSheet.popoverContainer)} />
				)}
			</div>
			{isAccountTemplate ? <CampaignSummaryFromContent card={card} /> : <CampaignSummaryFromContentShort card={card} />}
			<CampaignTemplateCardCta
				ctaText={ctaText}
				finraImage={FinraImage}
				isFinraReviewed={isFinraReviewed}
				card={card}
				templateType={Api.TemplateType.Email}
			/>
			{isMyTemplate && showKeywords ? (
				<TemplateKeywords
					templateId={card.id}
					industry={industry}
					keywords={card?.keywords}
					invalidateTemplates={handleInvalidateTemplates}
					readOnly={!canEditKeywords}
				/>
			) : null}
			{card.scope === Api.TemplateScope.Account && card.creator?.id !== userSession.user?.id ? (
				<div className={css(styleSheet.sharedByFooter)}>
					{card.creator?.firstName || card.creator?.lastName
						? `Shared by ${card.creator?.firstName} ${card.creator?.lastName}`
						: 'Shared'}
				</div>
			) : null}
		</div>
	);
};

export const CampaignTemplateCard = inject(ImpersonationContextKey)(CampaignTemplateCardBase);
